import { Controller } from '@hotwired/stimulus'

// Buscador con filtros de la ficha de escuela de negocios (ENH-RNK-5363), con la barra del
// prototipo de la tarjeta: la lupa abre el campo, cada faceta es un desplegable y en móvil
// el embudo abre una hoja de filtros a pantalla completa.
//
// El filtrado sigue siendo server-side: cada cambio reenvía el mismo formulario GET dentro del
// turbo-frame `programas-de-la-escuela`, que envuelve solo el buscador y su listado, así que
// filtrar no repinta la ficha entera —ni la cabecera, ni las otras pantallas— y la URL avanza
// sola (`data-turbo-action="advance"` del frame). Sin JavaScript el formulario es un GET normal
// con todas las facetas desplegadas y su botón «Filtrar»: es progressive enhancement, y por
// eso el diseño de barra lo enciende `data-mejorado`, que ponemos aquí y no en el servidor.
//
// El formulario vive DENTRO del frame que recargamos, así que cada respuesta destruye y
// recrea este controlador. Todo lo que tiene que sobrevivir al swap —foco, caret, faceta
// desplegada, hoja abierta— se guarda a nivel de módulo, no en la instancia. El patrón es el
// de `cotizaciones_search_controller`, que resolvió lo mismo para el buscador de cotizaciones.
const estado = {
  restaurar: false, valor: null, inicio: 0, fin: 0, enviado: null,
  faceta: null, hoja: false
}

// 300 ms es lo que usa el buscador de cotizaciones para "sugerir mientras buscas". Las
// casillas no se debouncean igual: se marcan de una en una y esperar 300 ms por cada una se
// nota como que la página no responde, pero sin nada de espera dos clics seguidos lanzan dos
// peticiones y la primera repinta el formulario a media selección.
const ESPERA_TECLEO_MS = 300
const ESPERA_CASILLA_MS = 120

// Los nombres de los programas son largos («Máster en Bolsa y Mercados Financieros») y la
// búsqueda es por palabras del permalink: con una, dos o tres letras no acota nada —devuelve
// casi el catálogo entero— y sí cuesta una consulta por pulsación. El campo vacío es la
// excepción: hay que enviarlo para restaurar el listado sin filtrar.
const MINIMO_DE_LETRAS = 4

// La zona que se repinta al filtrar. Mientras vuela la petición se le pone `data-loading`, que
// es lo que el proyecto ya usa para velar una zona y pintarle la rueda encima (el comparador
// hace lo mismo con su listado).
const ZONA_DE_RESULTADOS = '.rnk-Escuelas_Resultados'
const AVISO_DE_ESPERA = 'Filtrando programas'

// En el camino feliz el velo se va solo: Turbo reemplaza el frame entero y el HTML del
// servidor viene sin la marca. Pero si la petición no llega a repintar —se cae la red, o la
// respuesta no trae un frame con este id— nadie la quita, y el velo tapa buscador y listado
// sin dejar nada que pulsar hasta que el visitante recargue. Estos son los dos avisos que da
// Turbo en ese caso.
const FALLOS_DE_TURBO = ['turbo:fetch-request-error', 'turbo:frame-missing']

const FOCALIZABLES = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'

export default class extends Controller {
  static targets = ['campo', 'enviar', 'faceta', 'hoja']

  connect() {
    // Turbo va en modo opt-in en esta app: `packs/hotwired.js` desactiva Drive y marca
    // `data-turbo="false"` a todo formulario dentro de un turbo-frame que no diga lo
    // contrario. Sin estos atributos cada filtro sería una recarga completa.
    this.element.setAttribute('data-turbo', 'true')
    this.element.setAttribute('data-turbo-frame', 'programas-de-la-escuela')
    this.element.dataset.mejorado = ''
    // Marca la caja de facetas como hoja: en móvil es lo que la convierte en el panel a
    // pantalla completa, y sin JavaScript no lo es (ni habría con qué abrirla ni cerrarla).
    this.hojaTarget.dataset.hoja = ''

    // Con JavaScript el filtrado es en vivo, así que el botón que aplica sobra. Se sirve
    // siempre en el HTML para que la ficha siga filtrando sin JavaScript.
    this.enviarTarget.hidden = true

    if (estado.enviado === null) estado.enviado = this.termino
    this.facetaTargets.forEach((faceta) => this.cerrarFaceta(faceta))
    this.restaurarDespliegue()
    this.restaurarEscritura()

    this.fueraEscucha = (evento) => this.alPulsarFuera(evento)
    this.teclaEscucha = (evento) => this.alPulsarTecla(evento)
    this.falloEscucha = () => this.retirarAvisoDeEspera()
    document.addEventListener('click', this.fueraEscucha)
    document.addEventListener('keydown', this.teclaEscucha)
    FALLOS_DE_TURBO.forEach((fallo) => document.addEventListener(fallo, this.falloEscucha))
  }

  disconnect() {
    clearTimeout(this.temporizador)
    document.removeEventListener('click', this.fueraEscucha)
    document.removeEventListener('keydown', this.teclaEscucha)
    FALLOS_DE_TURBO.forEach((fallo) => document.removeEventListener(fallo, this.falloEscucha))
    this.desbloquearFondo()
  }

  // ---- Búsqueda por texto -------------------------------------------------

  abrirBusqueda() {
    this.element.dataset.busqueda = 'abierta'
    this.campoTarget.focus()
  }

  // Cerrar la lupa borra el término, como en el prototipo: si la barra se cierra con algo
  // buscado, el listado queda filtrado por un texto que ya no se ve en ninguna parte.
  cerrarBusqueda() {
    this.element.dataset.busqueda = 'cerrada'
    if (this.termino === '') return

    this.campoTarget.value = ''
    this.enviar()
  }

  buscar() {
    this.recordarEscritura()
    clearTimeout(this.temporizador)
    if (this.termino === estado.enviado || !this.terminoBuscable) return

    this.temporizador = setTimeout(() => this.enviar(), ESPERA_TECLEO_MS)
  }

  // ---- Facetas ------------------------------------------------------------

  desplegar(evento) {
    const faceta = evento.currentTarget.closest('[data-faceta]')
    const abierta = faceta.dataset.desplegada === 'true'

    this.facetaTargets.forEach((otra) => this.cerrarFaceta(otra))
    if (abierta) {
      estado.faceta = null
      return
    }

    faceta.dataset.desplegada = 'true'
    faceta.querySelector('[aria-expanded]').setAttribute('aria-expanded', 'true')
    estado.faceta = faceta.dataset.faceta
  }

  // Vaciar el filtrado no necesita ir al servidor a por la ficha entera: se desmarca todo, se
  // borra el texto y se reenvía el formulario, que repinta solo el listado. El enlace sigue
  // siendo un enlace para quien no tenga JavaScript.
  limpiar(evento) {
    evento.preventDefault()
    clearTimeout(this.temporizador)

    this.element.querySelectorAll('input[type="checkbox"]:checked').forEach((casilla) => { casilla.checked = false })
    this.campoTarget.value = ''
    estado.faceta = null
    this.facetaTargets.forEach((faceta) => this.cerrarFaceta(faceta))
    this.enviar()
  }

  filtrar() {
    clearTimeout(this.temporizador)
    this.temporizador = setTimeout(() => this.enviar(), ESPERA_CASILLA_MS)
  }

  // ---- Hoja de filtros (móvil) --------------------------------------------

  abrirHoja() {
    estado.hoja = true
    this.hojaTarget.dataset.abierta = 'true'
    this.bloquearFondo()
    this.focoPrevio = document.activeElement
    this.primerFocalizable()?.focus()
  }

  cerrarHoja() {
    estado.hoja = false
    delete this.hojaTarget.dataset.abierta
    this.desbloquearFondo()
    this.focoPrevio?.focus()
  }

  // ---- Teclado y clic fuera -----------------------------------------------

  alPulsarTecla(evento) {
    if (evento.key === 'Escape') return this.alEscapar()
    if (evento.key === 'Tab' && this.hojaAbierta) this.atraparFoco(evento)
  }

  // Escape cierra lo más interior que haya abierto, que es lo que el visitante espera
  // deshacer: primero el desplegable, luego la hoja, luego la búsqueda.
  alEscapar() {
    const desplegada = this.facetaTargets.find((faceta) => faceta.dataset.desplegada === 'true')
    if (desplegada) {
      this.cerrarFaceta(desplegada)
      estado.faceta = null
      return
    }
    if (this.hojaAbierta) return this.cerrarHoja()
    if (this.element.dataset.busqueda === 'abierta') this.cerrarBusqueda()
  }

  // Cualquier clic que no caiga dentro de la faceta abierta la cierra: su propio botón, el
  // resto de la barra, el contador o la página. Lo único que no la cierra es un clic dentro de
  // su panel, que es donde se marcan los valores.
  alPulsarFuera(evento) {
    const desplegada = this.facetaTargets.find((faceta) => faceta.dataset.desplegada === 'true')
    if (!desplegada || desplegada.contains(evento.target)) return

    this.cerrarFaceta(desplegada)
    estado.faceta = null
  }

  atraparFoco(evento) {
    const candidatos = [...this.hojaTarget.querySelectorAll(FOCALIZABLES)]
    // `offsetParent` nulo es lo que descarta lo que el CSS esconde: en escritorio la hoja no
    // existe como tal y sus piezas de móvil están ocultas.
    const focalizables = candidatos.filter((nodo) => nodo.offsetParent !== null)
    if (focalizables.length === 0) return

    const primero = focalizables[0]
    const ultimo = focalizables[focalizables.length - 1]
    const saliendo = evento.shiftKey ? document.activeElement === primero : document.activeElement === ultimo
    if (!saliendo) return

    evento.preventDefault()
    ;(evento.shiftKey ? ultimo : primero).focus()
  }

  // ---- Envío y estado que sobrevive al repintado --------------------------

  enviar() {
    this.avisarDeEspera()
    estado.enviado = this.termino
    // Solo devolvemos el foco si el visitante lo tenía en el campo: si marcó una casilla no
    // queremos robárselo de donde está.
    estado.restaurar = document.activeElement === this.campoTarget
    this.recordarEscritura()
    this.element.requestSubmit()
  }

  // En el camino feliz no hay que quitarlo: el repintado trae el HTML del servidor, que viene
  // sin él. Lo retira `retirarAvisoDeEspera` cuando no llega a haber repintado.
  avisarDeEspera() {
    const zona = this.element.closest(ZONA_DE_RESULTADOS)
    if (!zona) return

    zona.dataset.loading = ''
    zona.setAttribute('loading', AVISO_DE_ESPERA)
  }

  retirarAvisoDeEspera() {
    const zona = this.element.closest(ZONA_DE_RESULTADOS)
    if (!zona) return

    delete zona.dataset.loading
    zona.removeAttribute('loading')
  }

  recordarEscritura() {
    estado.valor = this.campoTarget.value
    estado.inicio = this.campoTarget.selectionStart
    estado.fin = this.campoTarget.selectionEnd
  }

  // Tras el swap el campo es un nodo nuevo con el `q` de la petición. Si el visitante siguió
  // tecleando mientras volaba, su texto manda: lo devolvemos, recolocamos el caret y
  // reprogramamos el envío del término real.
  restaurarEscritura() {
    if (!estado.restaurar) return

    estado.restaurar = false
    const campo = this.campoTarget
    if (estado.valor !== null && estado.valor !== campo.value) campo.value = estado.valor
    campo.focus()
    campo.setSelectionRange(estado.inicio, estado.fin)
    if (this.termino !== estado.enviado) this.buscar()
  }

  // Marcar una casilla repinta la página entera, así que sin esto el desplegable se cerraría
  // al primer clic y la hoja de móvil desaparecería en cuanto se tocara un filtro.
  restaurarDespliegue() {
    if (estado.hoja) this.abrirHoja()
    if (!estado.faceta) return

    const faceta = this.facetaTargets.find((nodo) => nodo.dataset.faceta === estado.faceta)
    if (!faceta) return

    faceta.dataset.desplegada = 'true'
    faceta.querySelector('[aria-expanded]').setAttribute('aria-expanded', 'true')
  }

  cerrarFaceta(faceta) {
    delete faceta.dataset.desplegada
    faceta.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', 'false')
  }

  bloquearFondo() {
    document.documentElement.style.overflow = 'hidden'
  }

  desbloquearFondo() {
    document.documentElement.style.overflow = ''
  }

  primerFocalizable() {
    return this.hojaTarget.querySelector(FOCALIZABLES)
  }

  get hojaAbierta() {
    return this.hojaTarget.dataset.abierta === 'true'
  }

  get terminoBuscable() {
    return this.termino.length === 0 || this.termino.length >= MINIMO_DE_LETRAS
  }

  get termino() {
    return this.campoTarget.value.trim()
  }
}
